import "./PollyAuthoring.scss";

import { Alert, Text } from "@fluentui/react-northstar";
import { createOrdinalQuestion } from "@teams/client-shared";
import {
  PollyAuthoringOrigin,
  Question,
  QuestionType,
  TeamsSurveyDraft,
  TeamsSurveyTypes,
} from "@teams/types";
import { QuestionnaireTemplateDTO, SurveyDTO } from "@teams/types/api";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useQuestionTemplates } from "../../../../_shared/actions/useQuestionTemplates";
import { useUserInfo } from "../../../../_shared/actions/useUserInfo";
import { TeamsLoader } from "../../../../_shared/components/TeamsLoader";
import { isError } from "../../../../_shared/services/ChannelApi";
import { scheduleApi } from "../../../../_shared/services/ScheduleApi";
import { surveyApi } from "../../../../_shared/services/SurveyApi";
import { templatesApi } from "../../../../_shared/services/TemplatesApi";
import { useTeamsContext } from "../../../../_shared/state/TeamsContextProvider";
import {
  DEFAULT_AGILE_STANDUP_SURVEY,
  DEFAULT_QUESTION_SURVEY_NOMINAL,
  DEFAULT_QUESTION_SURVEY_OPEN,
  DEFAULT_QUESTION_SURVEY_ORDINAL,
  DEFAULT_QUIZ_SURVEY,
} from "../../../../_shared/utils/Authoring";
import { isMobileClient } from "../../../../_shared/utils/clientHelper";
import { clientEventTracker } from "../../../../_shared/utils/clientTelemetry";
import { AgilePollyAuthoringForm } from "../../../../agile/pages/components/AgilePollyAuthoringForm";
import { TriviaCreateForm } from "../../components/trivia/TriviaCreateForm";
import { HotTakeCreateForm } from "../../Meetings/components/hotTake/HotTakeCreateForm";
import { IcebreakerCreateForm } from "../../Meetings/components/icebreaker/IcebreakerCreateForm";
import { QnaCreateForm } from "../../Meetings/components/qna/QnaCreateForm";
import { PollyReOpenPage } from "./../PollyReOpenPage";
import {
  AuthoringFields,
  PollySurveyAuthoringForm,
  PollySurveyAuthoringProps,
} from "./../PollySurveyAuthoringForm";
import { useTracking } from "./hooks/useTracking";

type Props = {
  type: TeamsSurveyTypes;
  questionType?: QuestionType;
  questionTemplateId?: string;
  draftId?: string;
  surveyId?: string;
  templateId?: string;
  messageId?: string;
  externalError?: string;
  apiOnly?: boolean;
  onCreated: (
    usedTaskSubmit?: boolean,
    surveyId?: string,
    wasUpdate?: boolean,
    isRecurring?: boolean
  ) => void;
  formSettings?: Partial<PollySurveyAuthoringProps>;
  trackingSource: string;
  skipInstrumentation: boolean;
  isPersonal: boolean;
  authoringOrigin: PollyAuthoringOrigin;
  reOpen?: boolean;
  isEdit?: boolean;
  duplicate?: boolean;
  showHeader?: boolean;
  customRecipient?: boolean;
};

export const PollyAuthoring: React.FC<Props> = props => {
  const {
    type,
    questionType,
    questionTemplateId,
    onCreated,
    formSettings,
    messageId,
    draftId,
    surveyId,
    templateId,
    externalError,
    apiOnly,
    trackingSource,
    authoringOrigin,
    skipInstrumentation,
    isPersonal,
    reOpen,
    isEdit,
    duplicate,
    showHeader,
    customRecipient,
  } = props;

  const { teamsContext } = useTeamsContext();
  const { user } = useUserInfo();
  const { questionTemplates, isQuestionTemplatesLoading, loadQuestionTemplates } =
    useQuestionTemplates();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<AuthoringFields>(
    DEFAULT_QUESTION_SURVEY_NOMINAL
  );
  const [referenceTemplate, setReferenceTemplate] = useState<QuestionnaireTemplateDTO>();
  const [templateName, setTemplateName] = useState<string>();
  const isMobile = isMobileClient(teamsContext);
  const [errorLoadingPolly, setErrorLoadingPolly] = useState<boolean>(false);
  const [parentSurvey, setParentSurvey] = useState<SurveyDTO>();
  const [duplicateSurvey, setDuplicateSurvey] = useState<SurveyDTO>();

  useTracking({
    type,
    apiOnly,
    draftId,
    duplicate,
    isEdit,
    reOpen,
    surveyId,
    templateId,
    trackingSource,
    authoringOrigin,
    skipInstrumentation,
    isPersonal,
  });

  const isMeetingTabOrSidePanel = useMemo(() => {
    if (!teamsContext) {
      return false;
    }

    // handle for channel meetings
    if (teamsContext.channel?.id && teamsContext.meeting?.id) {
      return true;
    }

    return (
      teamsContext.meeting?.id &&
      (teamsContext.app.appLaunchId ||
        teamsContext.page.frameContext === "sidePanel" ||
        teamsContext.page.frameContext === "task")
    );
  }, [teamsContext]);

  // chatId for calendar meetings contains meeting_ prefix. Group chat meetings don't
  const isGroupChatMeeting = useMemo(() => {
    return (
      !teamsContext.channel?.id &&
      teamsContext.chat?.id &&
      teamsContext.meeting?.id &&
      teamsContext.chat?.id.indexOf("meeting_") === -1
    );
  }, [teamsContext]);

  useEffect(() => {
    if (!templateId && !surveyId) {
      loadQuestionTemplates();
    }
  }, [templateId, surveyId, loadQuestionTemplates]);

  useEffect(() => {
    let setValues: AuthoringFields;

    switch (type) {
      case "poll":
        switch (questionType) {
          case "open-ended":
            setValues = DEFAULT_QUESTION_SURVEY_OPEN;
            break;

          case "ordinal":
            if (!questionTemplates || isQuestionTemplatesLoading) {
              return;
            }

            const ordinalTemplate = questionTemplates.find(t => t.id === questionTemplateId);
            if (!ordinalTemplate) {
              setValues = DEFAULT_QUESTION_SURVEY_NOMINAL;
            } else {
              setValues = { ...DEFAULT_QUESTION_SURVEY_ORDINAL };
              setValues.questions = [
                createOrdinalQuestion(
                  {
                    templateId: ordinalTemplate.id,
                    label: ordinalTemplate.label,
                  },
                  undefined,
                  ordinalTemplate
                ),
              ];
            }
            break;

          default:
            setValues = DEFAULT_QUESTION_SURVEY_NOMINAL;
            break;
        }
        break;

      case "survey":
        setValues = DEFAULT_QUESTION_SURVEY_NOMINAL;
        break;
      case "quiz":
        setValues = DEFAULT_QUIZ_SURVEY;
        break;
      case "agile-standup":
        setValues = DEFAULT_AGILE_STANDUP_SURVEY;
        break;
      default:
        return;
    }

    // default to manual send if in meeting side panel and not in a group chat meeting
    // and authoring not initiated via composeExtension or messageAction
    if (
      isMeetingTabOrSidePanel &&
      !isGroupChatMeeting &&
      authoringOrigin !== "composeExtension" &&
      authoringOrigin !== "messageAction"
    ) {
      setValues = { ...setValues, sendAt: undefined };
    }

    setInitialValues(setValues);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, questionTemplates, questionTemplateId, isQuestionTemplatesLoading]);

  const loadPollyFromServer = useCallback(
    async (surveyId: string) => {
      setIsLoading(true);
      const response = await surveyApi.survey(teamsContext, surveyId);
      setIsLoading(false);

      if (!isError(response)) {
        const { survey } = response;

        checkQuestionFields(survey.questions);

        if (survey.deliverAt) {
          survey.deliverAt = new Date(survey.deliverAt);
        }
      }

      return response;
    },
    [teamsContext]
  );

  const loadTemplateFromServer = useCallback(
    async templateId => {
      setIsLoading(true);
      const template = await templatesApi.getTemplate(teamsContext, templateId);
      setIsLoading(false);
      return template;
    },
    [teamsContext]
  );

  const setInitialValuesFromPollyId = useCallback(
    async surveyId => {
      const response = await loadPollyFromServer(surveyId);

      if (isError(response)) {
        setErrorLoadingPolly(true);
        return;
      }

      const { survey } = response;
      const initialValues = getFieldsFromSurveyOrDraft(survey);

      if (duplicate) {
        setDuplicateSurvey(survey);
      } else {
        setParentSurvey(survey);

        if (survey.responseCount > 0) {
          initialValues.notifyUsersOnEdit = true;
        }
      }

      if (survey.scheduleId) {
        const schedule = await scheduleApi.getSeriesById(teamsContext, survey.scheduleId);

        if (isError(schedule)) {
          setErrorLoadingPolly(true);
          return;
        }

        if (schedule?.rrule) {
          initialValues.rrule = schedule.rrule;
        }

        if (schedule?.rruleDTStart) {
          initialValues.sendAt = moment(schedule?.rruleDTStart).toDate();
        }

        if (schedule?.surveyOpenDuration) {
          initialValues.openDuration = schedule?.surveyOpenDuration;
        } else {
          const templateId = schedule?.template.id;
          const template = await loadTemplateFromServer(templateId);

          if (isError(template)) {
            setErrorLoadingPolly(true);
            return;
          }

          initialValues.openDuration = template.openDuration || "P1D";
        }
      }

      setInitialValues(initialValues);
    },
    [duplicate, loadPollyFromServer, loadTemplateFromServer, teamsContext]
  );

  useEffect(() => {
    if (!draftId) {
      return;
    }

    (async () => {
      setIsLoading(true);
      const response = await surveyApi.draft(teamsContext, draftId);
      setIsLoading(false);

      if (isError(response)) {
        setErrorLoadingPolly(true);
        return;
      }

      const { draft } = response;
      checkQuestionFields(draft.questions);

      draft.deliverAt = draft.deliverAt ? new Date(draft.deliverAt) : new Date();

      const fields = getFieldsFromSurveyOrDraft(draft);

      if (draft.schedule) {
        fields.rrule = draft.schedule.rrule;
        fields.openDuration = draft.schedule.openDuration;
        fields.sendAt = moment(draft.schedule.rruleDTStart).toDate();
      }

      setInitialValues(fields);
    })();
  }, [draftId, teamsContext]);

  useEffect(() => {
    if (!surveyId) {
      return;
    }

    setInitialValuesFromPollyId(surveyId);
  }, [surveyId, setInitialValuesFromPollyId]);

  useEffect(() => {
    if (!templateId) {
      return;
    }

    setTemplateName(undefined);

    (async () => {
      const template = await loadTemplateFromServer(templateId);

      if (!isError(template)) {
        checkQuestionFields(template.questions);
        setReferenceTemplate(template);
        setTemplateName(template.templateName);
        setInitialValues({
          type: template.type,
          title: template.title,
          description: template.description,
          questions: template.questions,
          resultsVisibility: template.resultsVisibility,
          openDuration: template.openDuration || undefined,
          anonymity: template.anonymity,
          sendAt:
            isMeetingTabOrSidePanel &&
            !isGroupChatMeeting &&
            authoringOrigin !== "composeExtension" &&
            authoringOrigin !== "messageAction"
              ? undefined
              : new Date(),
          commentsAppeal: (template.commentsAppeal as string) || "",
          commentsEnabled: template.commentsEnabled,
          reminders: template.reminders || [],
          rrule: "",
          deliveryMethod: template.deliveryPreference || "in-group",
          notifyUsersOnEdit: false,
          removeExistingVotesOnEdit: false,
        });

        clientEventTracker.track("template used", user, {
          id: template.id,
          type: template.type,
          templateType: template.templateType,
          title: template.templateType !== "user" ? template.title : undefined,
          questions: template.questions.length,
          trackingSource,
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  if (reOpen) {
    if (!surveyId) {
      return <Alert content="Missing survey details for reopening." danger />;
    } else {
      return (
        <TeamsLoader isLoading={!parentSurvey} loadingMessage="Loading survey details...">
          <PollyReOpenPage survey={parentSurvey!} />
        </TeamsLoader>
      );
    }
  }

  if (errorLoadingPolly && (type === "poll" || type === "survey" || type === "quiz")) {
    return (
      <Alert
        danger
        content={
          <Text content="There was an error loading this Polly for editing" align="center" />
        }
      ></Alert>
    );
  }

  if (templateName && ["qna", "trivia", "agile-standup"].includes(type)) {
    console.warn("template title not being displayed");
  }

  const headerOptions = !showHeader
    ? { showHeader: false as const }
    : { showHeader: true as const, isEdit: isEdit ?? false, templateId, trackingSource };

  return (
    <>
      {(type === "poll" || type === "survey" || type === "quiz") && (
        <PollySurveyAuthoringForm
          compressed={isMobileClient(teamsContext)}
          hideSaveAsTemplate={
            referenceTemplate?.role === "read" && referenceTemplate?.tenantId !== "global"
          }
          isGlobalTemplate={referenceTemplate?.tenantId === "global"}
          {...formSettings}
          defaultValues={initialValues}
          showLoading={isLoading}
          externalError={externalError}
          messageId={messageId}
          referenceTemplateId={referenceTemplate?.id}
          onSaved={onCreated}
          useApiOnly={apiOnly}
          allowManualSend={
            !!(
              isMeetingTabOrSidePanel &&
              !isGroupChatMeeting &&
              authoringOrigin !== "composeExtension" &&
              authoringOrigin !== "messageAction"
            )
          }
          allowScheduledSend={teamsContext.page.frameContext !== "sidePanel"}
          isMobile={isMobile}
          surveyId={surveyId}
          isEdit={isEdit}
          parentSurvey={parentSurvey}
          duplicate={duplicate}
          templateName={templateName}
          headerOptions={headerOptions}
          trackingFields={{ authoringOrigin, trackingSource, isPersonal }}
          customRecipient={customRecipient}
        />
      )}

      {type === "trivia" && (
        <TriviaCreateForm
          onCreated={(survey: SurveyDTO) => {
            onCreated(false, survey.id);
          }}
          trackingFields={{ authoringOrigin, trackingSource, isPersonal }}
          headerOptions={headerOptions}
        />
      )}

      {type === "qna" && (
        <QnaCreateForm
          onCreated={(survey: SurveyDTO) => {
            onCreated(false, survey.id, !!parentSurvey);
          }}
          editSurvey={parentSurvey}
          trackingFields={{ authoringOrigin, trackingSource, isPersonal }}
          headerOptions={headerOptions}
        />
      )}

      {type === "agile-standup" && (
        <AgilePollyAuthoringForm
          surveyData={initialValues}
          reminderSizeLimit={25}
          showLoading={isLoading}
          headerOptions={headerOptions}
        />
      )}

      {type === "icebreaker" && (
        <IcebreakerCreateForm
          onFormCompleted={(survey: SurveyDTO) => {
            onCreated(false, survey.id, !!parentSurvey);
          }}
          editSurvey={parentSurvey}
          duplicateSurvey={duplicateSurvey}
          trackingFields={{ authoringOrigin, trackingSource, isPersonal }}
          headerOptions={headerOptions}
        />
      )}

      {type === "hot-take" && (
        <HotTakeCreateForm
          onFormCompleted={(survey: SurveyDTO) => {
            onCreated(false, survey.id, !!parentSurvey);
          }}
          editSurvey={parentSurvey}
          duplicateSurvey={duplicateSurvey}
          trackingFields={{ authoringOrigin, trackingSource, isPersonal }}
          headerOptions={headerOptions}
        />
      )}
    </>
  );
};

function checkQuestionFields(questions: Question<QuestionType>[]) {
  questions.forEach(question => {
    if (!question.commentAppeal) {
      question.commentAppeal = "";
    }
  });
}

function getFieldsFromSurveyOrDraft(data: SurveyDTO | TeamsSurveyDraft): AuthoringFields {
  return {
    title: data.title,
    type: data.type,
    anonymity: data.anonymity,
    resultsVisibility: data.resultsVisibility,
    questions: data.questions,
    openDuration: data.openDuration,
    sendAt: data.deliverAt as Date,
    commentsEnabled: data.commentsEnabled,
    commentsAppeal: data.commentsAppeal || ("" as string),
    description: data.description || "",
    reminders: data.reminders || [],
    rrule: "",
    deliveryMethod: data.deliveryPreference || "in-group",
    notifyUsersOnEdit: false,
    removeExistingVotesOnEdit: false,
  };
}
